import Loader from 'components/Loader';
import { DASHBOARD_TYPES, HUB_ROLES, LOCAL_STORAGE_KEYS } from 'constants/global';
import ROUTES from 'constants/routes';
import useUser from 'hooks/useUser';
import DashboardLayout from 'layouts/DashboardLayout';
import OnboardLayout from 'layouts/OnboardLayout';
import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { getFromStorage } from 'utils';
import { convertToSlug } from 'utils/slug';

// Auth
const Login = lazy(() => import('pages/Auth/Login'));
const Register = lazy(() => import('pages/Auth/Register'));
const RecoverPassword = lazy(() => import('pages/Auth/RecoverPassword'));
const VerifyEmail = lazy(() => import('pages/Auth/VerifyEmail'));

// Retail
const RetailDashboard = lazy(() => import('pages/Retail'));
const Trades = lazy(() => import('pages/Retail/Trades'));
const Requisitions = lazy(() => import('pages/Retail/Requisitions'));
const Sales = lazy(() => import('pages/Retail/Sales'));
const Farmers = lazy(() => import('pages/Retail/Farmers'));
const Profile = lazy(() => import('pages/Profile'));
const FinanceRequests = lazy(() => import('pages/Retail/Finance/Requests'));
const Storage = lazy(() => import('pages/Retail/Storage'));
const Hubs = lazy(() => import('pages/Retail/Hubs'));
const ContributionMargin = lazy(() => import('pages/Retail/Metrics/ContributionMargin'));
const LocationsCM2 = lazy(() => import('pages/Retail/Metrics/LocationsCM2'));
const Inventory = lazy(() => import('pages/Retail/Metrics/Inventory'));
const RetailDeliveries = lazy(() => import('pages/Retail/Deliveries'));
const HubsMonthlySale = lazy(() => import('pages/Retail/Metrics/HubsMonthlySale'));
const AI = lazy(() => import('pages/AI'));

// Logistics
const LogisticsDashboard = lazy(() => import('pages/Logistics'));
const SupplyTrades = lazy(() => import('pages/Logistics/SupplyTrades'));
const CompanyAssets = lazy(() => import('pages/Logistics/CompanyAssets'));
const Deliveries = lazy(() => import('pages/Logistics/Deliveries'));
const RoutePlans = lazy(() => import('pages/Logistics/RoutePlans'));
const Expenses = lazy(() => import('pages/Logistics/Expenses'));
const Maintenance = lazy(() => import('pages/Logistics/Maintenance'));

// Hub
const HubSales = lazy(() => import('pages/RetailHub/Sales'));
const HubInventory = lazy(() => import('pages/RetailHub/Inventory'));
const HubRestock = lazy(() => import('pages/RetailHub/RestockHub'));
const HubDeliveries = lazy(() => import('pages/RetailHub/Deliveries'));

// Assets
const AssetsDashboard = lazy(() => import('pages/Asset'));
const AssetsList = lazy(() => import('pages/Asset/AssetsList'));
const Depreciation = lazy(() => import('pages/Asset/Depreciation'));
const WriteOff = lazy(() => import('pages/Asset/WriteOff'));
const Capitalization = lazy(() => import('pages/Asset/Capitalization'));

const App = () => {
    const { hasAllAccess, isWholeSale, isTrade, isSuperAdmin, user, isWholeSaleManager, isFinance } = useUser();
    const dashboardType = getFromStorage(LOCAL_STORAGE_KEYS.DASHBOARD_TYPE);
    const hubUser = user?.role === HUB_ROLES.AGENT || user?.role === HUB_ROLES.MANAGER;

    return (
        <Suspense
            fallback={
                <div className="h-screen flex items-center justify-center">
                    <Loader />
                </div>
            }
        >
            <Routes>
                <Route element={<OnboardLayout />}>
                    <Route path={ROUTES.LOGIN} element={<Login />} />
                    <Route path={ROUTES.REGISTER} element={<Register />} />
                    <Route path={ROUTES.RECOVER} element={<RecoverPassword />} />
                    <Route path={ROUTES.VERIFY_EMAIL} element={<VerifyEmail />} />
                </Route>

                <Route element={<DashboardLayout />}>
                    {hubUser ? (
                        <>
                            <Route path={ROUTES.HUB_SALES} element={<HubSales />} />
                            <Route path={ROUTES.HUB_RESTOCK} element={<HubRestock />} />
                            <Route path={ROUTES.HUB_INVENTORY} element={<HubInventory />} />
                            <Route path={ROUTES.HUB_DELIVERIES} element={<HubDeliveries />} />
                        </>
                    ) : (
                        <>
                            <Route
                                path={ROUTES.HOMEPAGE}
                                element={
                                    dashboardType === DASHBOARD_TYPES.WHOLESALE ? (
                                        <RetailDashboard />
                                    ) : dashboardType === DASHBOARD_TYPES.LOGISTICS ? (
                                        <LogisticsDashboard />
                                    ) : dashboardType === DASHBOARD_TYPES.ASSETS ? (
                                        <AssetsDashboard />
                                    ) : null
                                }
                            />
                            {dashboardType === DASHBOARD_TYPES.WHOLESALE ? (
                                <>
                                    {user?.role && (
                                        <>
                                            {(isTrade || hasAllAccess || isWholeSaleManager) && (
                                                <Route path={ROUTES.TRADES} element={<Trades />} />
                                            )}
                                            {(isWholeSale || hasAllAccess || isWholeSaleManager) && (
                                                <Route path={ROUTES.SALES} element={<Sales />} />
                                            )}
                                            <Route path={ROUTES.REQUISITIONS} element={<Requisitions />} />
                                            {(isWholeSale || hasAllAccess || isWholeSaleManager) && (
                                                <Route path={ROUTES.FARMERS} element={<Farmers />} />
                                            )}
                                            {(isSuperAdmin || isFinance) && (
                                                <>
                                                    <Route path={ROUTES.STORAGE} element={<Storage />} />
                                                    <Route path={ROUTES.HUBS} element={<Hubs />} />
                                                </>
                                            )}
                                            <Route path={ROUTES.RETAIL_DELIVERIES} element={<RetailDeliveries />} />
                                            {(hasAllAccess || isWholeSaleManager) && (
                                                <>
                                                    <Route
                                                        path={ROUTES.CONTRIBUTION_MARGIN}
                                                        element={<ContributionMargin />}
                                                    />
                                                    <Route path={ROUTES.LOCATIONS_CM2} element={<LocationsCM2 />} />
                                                    <Route
                                                        path={ROUTES.HUBS_MONTHLY_SALES}
                                                        element={<HubsMonthlySale />}
                                                    />
                                                    <Route path={ROUTES.INVENTORY} element={<Inventory />} />
                                                </>
                                            )}
                                            {hasAllAccess && (
                                                <Route
                                                    path={ROUTES.FINANCE_REQUISITIONS}
                                                    element={<FinanceRequests />}
                                                />
                                            )}
                                        </>
                                    )}
                                </>
                            ) : dashboardType === DASHBOARD_TYPES.LOGISTICS ? (
                                <>
                                    <Route path={ROUTES.SUPPLY_TRADES} element={<SupplyTrades />} />
                                    <Route path={ROUTES.ASSETS} element={<CompanyAssets />} />
                                    <Route path={ROUTES.DELIVERIES} element={<Deliveries />} />
                                    <Route path={ROUTES.EXPENSES} element={<Expenses />} />
                                    <Route path={ROUTES.MAINTENANCE} element={<Maintenance />} />
                                    <Route path={ROUTES.ROUTE_PLANS} element={<RoutePlans />} />
                                </>
                            ) : dashboardType === DASHBOARD_TYPES.ASSETS ? (
                                <>
                                    <Route path={ROUTES.ASSETS} element={<AssetsList />} />
                                    <Route path={ROUTES.DEPRECIATION} element={<Depreciation />} />
                                    <Route path={ROUTES.WRITE_OFF} element={<WriteOff />} />
                                    <Route path={ROUTES.CAPITALIZATION} element={<Capitalization />} />
                                </>
                            ) : null}
                            <Route path={ROUTES.AI} element={<AI />} />
                        </>
                    )}

                    <Route path={ROUTES.PROFILE} element={<Profile />} />
                </Route>

                <Route
                    path={'*'}
                    element={
                        <Navigate
                            to={
                                hubUser
                                    ? `/hub/${convertToSlug(user?.hubs?.length ? user.hubs[0] : user?.hub)}/sales`
                                    : ROUTES.HOMEPAGE
                            }
                        />
                    }
                />
            </Routes>
        </Suspense>
    );
};

export default App;
